/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.20.0
 */

import { Fragment, useCallback } from 'react';

import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';

import { IOptionItemProps } from '@components/common/Select/Select';
import FormFieldSelect from '@modules/form/components/FormFieldSelect';
import { CreateInquiryInitialValues, ECreateInquiryField as F } from '@modules/inquiry/constants';
import { createInquirySectionAndTopicsSelector } from '@modules/inquiry/selectors';
import { ICreateInquiryForm, ICreateInquiryInitialise } from '@modules/inquiry/types';
import { makeSelectCreateInquiryTranslation } from '@modules/internationalization/intSelectors';

import CreateInquiryFieldLabel from '../CreateInquiryFieldLabel';

interface ICreateInquireInitialAttributes extends ICreateInquiryInitialise {
    getAttributes: (topicCode: string, validateForm: () => void) => void;
}

const CreateInquiryInitialAttributes = (
    { getAttributes, ...params }: ICreateInquireInitialAttributes) => {

    const { data } = useSelector(createInquirySectionAndTopicsSelector(params));

    /* Translation */
    const { start: tInquiryStart } = useSelector(makeSelectCreateInquiryTranslation());

    /* Form Context */
    const { resetForm, values, validateForm } = useFormikContext<ICreateInquiryForm>();

    /* Handlers */
    const onSectionChange = useCallback((newSection: IOptionItemProps) => {
        values.file.clear();

        resetForm({
            values: { ...CreateInquiryInitialValues, [F.section]: newSection },
        });
        setTimeout(() => {
            validateForm();
        })
    }, [resetForm, validateForm, values.file]);

    const onTopicChange = useCallback((newTopic: IOptionItemProps) => {
        values.file.clear();

        resetForm({
            values: { ...CreateInquiryInitialValues, [F.section]: values[F.section], [F.topic]: newTopic },
        });
        getAttributes(newTopic.value.toString(), validateForm);
    }, [getAttributes, resetForm, values, validateForm]);

    return (
        <Fragment>
            <CreateInquiryFieldLabel
                required
                name={tInquiryStart.fields[F.section].label}
                prompt={tInquiryStart.fields[F.section].prompt}
            />
            <FormFieldSelect
                dataName="inquiry-field-section"
                errorKey={`${F.section}.value`}
                maxWidth='640px'
                name={F.section}
                options={data?.sections ?? []}
                placeholder={tInquiryStart.fields[F.section].placeholder}
                onSelect={onSectionChange}
            />

            {
                values[F.section]?.value
                && data?.topics[(values[F.section]?.value ?? '') as string]
                && (
                    <Fragment>
                        <CreateInquiryFieldLabel
                            required
                            name={tInquiryStart.fields[F.topic].label}
                            prompt={tInquiryStart.fields[F.topic].prompt}
                        />
                        <FormFieldSelect
                            dataName="inquiry-field-topic"
                            errorKey={`${F.topic}.value`}
                            maxWidth='640px'
                            name={F.topic}
                            options={data?.topics[(values[F.section]?.value ?? '') as string]}
                            placeholder={tInquiryStart.fields[F.topic].placeholder}
                            onSelect={onTopicChange}
                        />
                    </Fragment>
                )
            }
        </Fragment>
    );
};

export default CreateInquiryInitialAttributes;
